import type { ReactNode } from "react";
import { cn } from "./cn";
import { Ikon, type IkonAdi } from "./Ikon";

export interface SayfaBasligiProps {
  baslik: ReactNode;
  /** Başlığın altındaki tek satır gri açıklama ("Önce koç/öğrenci seç; ..."). */
  aciklama?: ReactNode;
  /** Soldaki karonun ikonu; verilmezse karo çizilmez. */
  ikon?: IkonAdi;
  /**
   * Karo türü. UNİKOÇ'ta iç ekranların çoğunda BEYAZ karo + mor ikon; Mağaza ve
   * Bonus Çarkı gibi ödül ekranlarında DEGRADE karo + beyaz ikon (tarama-2 §2.1).
   */
  ikonTuru?: "beyaz" | "degrade";
  /** Sağdaki ekran eylemleri — kesikli hayalet düğmeler, birincil düğme. */
  eylemler?: ReactNode;
  /**
   * Başlık seviyesi. Kabuk sayfanın TEK `h1`ini bu bileşenle çiziyor; ekran içinde
   * ikinci bir bant gerekirse (detay alt sayfası) `2` verilir, iki `h1` yarışmasın.
   */
  seviye?: 1 | 2;
  /** Bandın altına, aynı kartın içine giren hızlı bağlantılar (Ders İşlemleri ekranındaki gibi). */
  children?: ReactNode;
  className?: string;
}

/**
 * SAYFA BAŞLIĞI BANDI — UNİKOÇ kalıbı (sürüm 9a; tarama-1 §2.6, tarama-2 §2.8)
 *
 * Her iç ekranın tepesinde aynı kart: lavanta → pembe yatay degrade, solda ikon
 * karosu, kalın başlık + gri açıklama, sağda eylemler. Kalıbın TEK bileşende
 * durmasının sebebi eski paneldeki tutarsızlık: orada bant ekran ekran elle
 * yazılmıştı ve köşe/yükseklik/ikon boyu her ekranda biraz farklıydı.
 * Ekranlar bandı kendileri çizmiyor; `Kabuk` çiziyor, ekran yalnızca açıklama
 * ve eylem veriyor (apps/panel `SayfaBasligiEki`).
 */
export function SayfaBasligi({
  baslik,
  aciklama,
  ikon,
  ikonTuru = "beyaz",
  eylemler,
  seviye = 1,
  children,
  className,
}: SayfaBasligiProps) {
  const Baslik = `h${seviye}` as const;
  return (
    <header
      data-ui="sayfa-basligi"
      className={cn(
        "rounded-[1.5rem] bg-(image:--gradyan-unk-bant) shadow-(--golge-unk-kart)",
        "ring-notr-0/70 ring-1 ring-inset",
        "px-4 py-4 md:px-6 md:py-5",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        {ikon && (
          <span
            aria-hidden="true"
            className={cn(
              "grid size-12 shrink-0 place-items-center rounded-[0.875rem]",
              ikonTuru === "degrade"
                ? "text-notr-0 bg-(image:--gradyan-unk-birincil) shadow-(--golge-unk-birincil)"
                : "bg-notr-0 text-mor-500 shadow-kart",
            )}
          >
            <Ikon ad={ikon} className="size-6" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <Baslik className="text-metin-koyu text-[1.375rem] leading-tight font-bold tracking-[-0.01em] md:text-[1.625rem]">
            {baslik}
          </Baslik>
          {/*
            `:has(>:only-child:empty)` → gizli: Kabuk buraya BOŞ bir yuva (portal hedefi) koyuyor;
            ekran açıklama vermediyse boş paragraf 4 px'lik boşluk bırakmasın.
          */}
          {aciklama && (
            <p className="text-metin-ikincil mt-1 text-sm leading-snug [&:has(>:only-child:empty)]:hidden">
              {aciklama}
            </p>
          )}
        </div>
        {/* Eylemler SAĞDA; dar ekranda başlığın altına iniyor (flex-wrap). */}
        {eylemler && (
          /*
            ESNEK GENISLIK: onceden `shrink-0` idi — yuvaya genis bir parca (mini kart, uzun etiketli
            dugme) girince bant tasiyordu. Simdi kapsayici daralabilir (`min-w-0 max-w-full`) ve
            icerigi sarar; dar ekranda baslikla ayni satira sigmazsa alta iner.
          */
          <div className="flex max-w-full min-w-0 flex-wrap items-center gap-2 [&:has(>:only-child:empty)]:hidden">
            {eylemler}
          </div>
        )}
      </div>
      {children && <div className="mt-4 flex flex-wrap gap-2">{children}</div>}
    </header>
  );
}
